import React from 'react';
import styled from 'styled-components';

import { ApiBalanceHistoryEvent } from '#/api/types';
import { useCopyToClipboard } from '#/components/basic/icons/CopyToClipboard';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import VaultBalanceEventSign from '#/components/vaults/components/VaultBalanceEventSign';
import {
  BANKROLL_BRIDGE_SOURCE,
  COMPETITION_REWARD_BRIDGE_SOURCE,
  BUILDER_REWARD_SOURCE,
  POINTS_REWARD_BRIDGE_SOURCE,
  REWARDS_BRIDGE_SOURCE,
  EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET,
} from '#/config/constants';
import { truncateAddressInTheMiddle } from '#/lib/string';
import { UnreachableCaseError } from '#/lib/utils';

import downDownIcon from './icons/downDown.svg';

const TypeWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;
`;

const CopyableAddressWrap = styled.span<{ $copied: boolean }>`
  cursor: copy;
  color: ${({ $copied }) => ($copied ? 'var(--success)' : 'inherit')};
  &:hover {
    color: ${({ $copied }) => ($copied ? 'var(--success)' : 'var(--primary3)')};
    text-decoration: underline dotted;
    text-underline-offset: 2px;
  }
`;

function CopyableAddress({ address }: { address: string }) {
  const { onCopy, recentlyCopied } = useCopyToClipboard();
  return (
    <CopyableAddressWrap
      $copied={recentlyCopied}
      title={`${address} (Click to copy)`}
      onClick={event => {
        event.stopPropagation();
        onCopy(address);
      }}
    >
      {truncateAddressInTheMiddle(address)}
    </CopyableAddressWrap>
  );
}

export default function BalanceTransactionType({
  transaction,
}: {
  transaction: ApiBalanceHistoryEvent;
}) {
  const type = transaction.type;
  const isVault = !!transaction.vault?.manager;
  switch (type) {
    case 'transferFromWallet':
      return (
        <TypeWrap>
          {transaction.bridgeSource ===
          EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET ? (
            <IconWrap>
              <MaterialSymbol icon="arrow_downward" color="var(--success)" />
            </IconWrap>
          ) : (
            <>
              <img src={downDownIcon} style={{ width: 16, margin: 4 }} />
            </>
          )}
          <span>
            Transfer from <CopyableAddress address={transaction.sourceWallet} />
          </span>
        </TypeWrap>
      );
    case 'deposit':
      if (
        transaction.bridgeSource === BANKROLL_BRIDGE_SOURCE ||
        transaction.bridgeSource === REWARDS_BRIDGE_SOURCE ||
        transaction.bridgeSource === POINTS_REWARD_BRIDGE_SOURCE ||
        transaction.bridgeSource === COMPETITION_REWARD_BRIDGE_SOURCE ||
        transaction.bridgeSource === BUILDER_REWARD_SOURCE
      ) {
        return (
          <TypeWrap>
            <img src={downDownIcon} style={{ width: 16, margin: 4 }} />
            {transaction.bridgeSource === BANKROLL_BRIDGE_SOURCE
              ? 'Bankroll'
              : transaction.bridgeSource === REWARDS_BRIDGE_SOURCE
              ? 'Referral Reward'
              : transaction.bridgeSource === POINTS_REWARD_BRIDGE_SOURCE
              ? 'Points Reward'
              : transaction.bridgeSource === BUILDER_REWARD_SOURCE
              ? 'Builder Reward'
              : transaction.bridgeSource === COMPETITION_REWARD_BRIDGE_SOURCE
              ? 'Competition Reward'
              : ''}
          </TypeWrap>
        );
      }
    // Otherwise continue for standard Deposit & Pending one...
    // eslint-disable-next-line no-fallthrough
    case 'pendingDeposit':
      return (
        <TypeWrap>
          <IconWrap>
            {isVault ? (
              <VaultBalanceEventSign type="deposit" />
            ) : (
              <MaterialSymbol icon="arrow_downward" color="var(--success)" />
            )}
          </IconWrap>
          {isVault ? 'Vault Deposit' : 'Deposit'}
        </TypeWrap>
      );
    case 'transferToWallet':
      return (
        <TypeWrap>
          <IconWrap>
            <MaterialSymbol icon="arrow_upward" color="var(--main)" />
          </IconWrap>
          <span>
            Transfer to <CopyableAddress address={transaction.targetWallet} />
          </span>
        </TypeWrap>
      );
    case 'withdrawal':
      return (
        <TypeWrap>
          <IconWrap>
            {isVault ? (
              <VaultBalanceEventSign type="withdrawal" />
            ) : (
              <MaterialSymbol icon="arrow_upward" color="var(--main)" />
            )}
          </IconWrap>
          Withdrawal
        </TypeWrap>
      );
    case 'unknown':
      return <>Unknown</>;
    default:
      throw new UnreachableCaseError(type);
  }
}
